import React from "react";
import PropTypes from "prop-types";

/**
 * Error summary banner displayed at the top of a form.
 *
 * Lists all validation errors with clickable links that navigate to
 * the invalid field (switching tabs when needed for cross-tab errors).
 * Uses `role="alert"` so screen readers announce the summary when it
 * appears (WCAG 4.1.3 Status Messages, AA).
 *
 * @example
 * <ErrorSummary
 *   errors={[
 *     { fieldId: "event-name", fieldName: "Event Name", message: "Enter an event name.", tabLabel: "Basic Information" },
 *     { fieldId: "start-date", fieldName: "Start Date", message: "Choose a start date.", tabLabel: "Basic Information" }
 *   ]}
 *   onErrorClick={({ fieldId, tabKey }) => { ... }}
 * />
 */
export const ErrorSummary = ({ errors, onErrorClick }) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  const handleClick = (error) => (event) => {
    event.preventDefault();
    onErrorClick(error);
  };

  const problemWord = errors.length === 1 ? "problem" : "problems";

  return (
    <div className="error-summary" role="alert">
      <div className="error-summary__bar" />
      <div className="error-summary__content">
        <div className="error-summary__header">
          <i
            className="fa fa-exclamation-circle error-summary__icon"
            aria-hidden="true"
          />
          <p className="error-summary__title">
            {`There ${errors.length === 1 ? "is" : "are"} ${errors.length} ${problemWord} to fix`}
          </p>
        </div>
        <ul className="error-summary__list">
          {errors.map((error) => (
            <li key={error.fieldId} className="error-summary__item">
              <a
                href={`#${error.fieldId}`}
                className="error-summary__link"
                onClick={handleClick(error)}
              >
                {error.tabLabel
                  ? `${error.tabLabel} > ${error.fieldName}: ${error.message}`
                  : `${error.fieldName}: ${error.message}`}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

ErrorSummary.propTypes = {
  /** Array of error objects to display */
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      /** DOM id of the invalid field (used for focus and href) */
      fieldId: PropTypes.string.isRequired,
      /** Human-readable field name */
      fieldName: PropTypes.string.isRequired,
      /** Error message describing what's wrong */
      message: PropTypes.string.isRequired,
      /** Tab key for cross-tab navigation (e.g. "basic-information") */
      tabKey: PropTypes.string,
      /** Human-readable tab label shown in cross-tab errors (e.g. "Basic Information") */
      tabLabel: PropTypes.string
    })
  ),
  /** Called when a user clicks an error link. Receives the error object. */
  onErrorClick: PropTypes.func.isRequired
};
